import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

interface Props<T> {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  loading?: ReactNode;
  errorLabel?: string;
}

/**
 * Renders the children with the resolved query data, or graceful Dutch
 * loading/error fallbacks. Keeps per-screen error handling consistent and
 * matches the Phase 5 a11y pass (loading state is announced via aria-live).
 */
export function QueryBoundary<T>({
  query,
  children,
  loading,
  errorLabel = 'Gegevens niet beschikbaar.',
}: Props<T>) {
  if (query.isPending) {
    return (
      <div aria-live="polite" aria-busy="true" className="mono c-ink-3">
        {loading ?? 'Laden…'}
      </div>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <div role="alert" className="mono c-bad">
        {errorLabel}{' '}
        <button
          type="button"
          onClick={() => query.refetch()}
          className="mono"
          style={{ textDecoration: 'underline' }}
        >
          Opnieuw proberen
        </button>
      </div>
    );
  }
  return <>{children(query.data)}</>;
}
